import React, { useCallback, useEffect, useRef, useState } from "react"
import { Form, Modal, Button, FormProps, message } from "antd"
import { useConfig } from "../contexts/ConfigContext"
import { useRegister } from "../contexts/RegisterContext"
import { useSecureRequest } from "../hooks/useSecureRequest"
import { useCompany } from "../contexts/CompanyContext"
import { useUser } from "../contexts/UserContext"
import { addToUniquesArray, createId, getItemTaxes, getTotalTaxes, roundValue } from "../utils"
import { modules } from "../config/pages"
import { IMovementItemForm, IMovementRS, IMovementElementItem, defaultStorageItemValues } from "../models/storageModel"
import { movementStatus } from "../config/const"
import { UnifiedItemModal, StorageItemBasic } from "@ryo/ui"

import {
  mutateMovementQueries,
  getLists,
  getMovementPrefix,
  getItemOptionLabel,
  createOption,
} from "./movement-item/movementItemCatalogHelpers"
import MovementItemSearchSection from "./movement-item/MovementItemSearchSection"
import MovementItemFormFields from "./movement-item/MovementItemFormFields"

export interface AddMovementItemModalProps {
  isEdit?: boolean
}

function RegisterMovementModal({ isEdit: propIsEdit }: AddMovementItemModalProps = {}) {
  const { config } = useConfig()
  const { register, updateRegister } = useRegister()
  const { company } = useCompany()
  const { user } = useUser()
  const { securePost } = useSecureRequest()
  const idCounter = useRef(-1)
  const getNextId = () => --idCounter.current
  const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null)
  const {
    currentMovementItem,
    currentPending,
    taxesOptions,
  } = register
  
  const [form] = Form.useForm<IMovementItemForm>()
  const [messageApi, contextHolder] = message.useMessage()
  const mod = config.module
  const [options, setOptions] = useState<any[]>([])
  const [searchValue, setSearchValue] = useState({ label: "", option: { uid: "" } })
  const [storageElement, setStorageElement] = useState(defaultStorageItemValues)
  const [storageElementList, setStorageElementList] = useState(register.registerList)
  const [loadingOk, setLoadingOk] = useState(false)
  const [loadingSearch, setLoadingSearch] = useState(false)
  const [loadingDelete, setLoadingDelete] = useState(false)
  const [disableSearch, setDisableSearch] = useState(!!currentMovementItem?.document_id)
  const [selectedItem, setSelectedItem] = useState(!!storageElement.id)
  const [showItemModal, setShowItemModal] = useState(false)
  const [activeTypeFilter, setActiveTypeFilter] = useState<string>(() => register.initialItemTypeTab || "ALL")
  const activeTypeFilterRef = useRef(activeTypeFilter)
  const searchRequestIdRef = useRef(0)
  const initialSearchDoneRef = useRef(false)
  const [modalPreType, setModalPreType] = useState<string | undefined>(undefined)
  const [modalPreName, setModalPreName] = useState<string | undefined>(undefined)
  const { itemList, pendingList } = getLists(mod, register)
  const isEdit = propIsEdit ?? !!currentMovementItem?.document_id
  let newPending = true

  const executeSearch = useCallback(async (text: string, typeFilterOverride?: string) => {
    if (!company.current?.uid) return
    const currentRequestId = ++searchRequestIdRef.current
    setLoadingSearch(true)
    try {
      const currentType = typeFilterOverride ?? activeTypeFilterRef.current
      const filterPayload: any = {
        company: company.current?.uid,
        search: text.trim(),
        type: "full",
        module: config.module,
        hitsPerPage: 30,
      }
      if (config.module === modules.RegisterExit) filterPayload.sellItem = true
      if (config.module === modules.RegisterEntry) filterPayload.purchaseItem = true
      if (currentType && currentType !== "ALL") {
        filterPayload.registerType = currentType
      }

      const itemsResponse = await securePost('/inventory/item/search-items', filterPayload)
      if (currentRequestId !== searchRequestIdRef.current) return

      if (itemsResponse.status === 200 && Array.isArray(itemsResponse.itemList)) {
        setStorageElementList(itemsResponse.itemList)
        const newOptions = itemsResponse.itemList.map((item: any) =>
          createOption(item, config.module, register.categoryOptions, register.displayOptions, register.umOptions)
        )
        setOptions(newOptions)
      } else {
        setStorageElementList([])
        setOptions([])
      }
    } catch (error) {
      console.error("Error realizando la búsqueda:", error)
    } finally {
      if (currentRequestId === searchRequestIdRef.current) {
        setLoadingSearch(false)
      }
    }
  }, [company.current?.uid, config.module, register.categoryOptions, register.displayOptions, register.umOptions, securePost])

  useEffect(() => {
    if (register.registerList?.length && !selectedItem && !isEdit && storageElementList.length === 0 && activeTypeFilter === 'ALL') {
      setStorageElementList(register.registerList)
    }
  }, [register.registerList, selectedItem, isEdit, activeTypeFilter, storageElementList.length])

  useEffect(() => {
    if (!initialSearchDoneRef.current && company.current?.uid && !isEdit && !storageElement.id) {
      initialSearchDoneRef.current = true
      const initialTab = register.initialItemTypeTab || 'ALL'
      setActiveTypeFilter(initialTab)
      activeTypeFilterRef.current = initialTab
      void executeSearch("", initialTab)
    }
  }, [company.current?.uid, isEdit, storageElement.id, executeSearch, register.initialItemTypeTab])

  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current)
      }
    }
  }, [])

  useEffect(() => {
    const newOptions = storageElementList.map((item: any) =>
      createOption(item, mod, register.categoryOptions, register.displayOptions, register.umOptions)
    )
    setOptions(newOptions)
  }, [storageElementList, mod, register.categoryOptions, register.displayOptions, register.umOptions])

  useEffect(() => {
    if (isEdit && currentMovementItem) {
      const label = getItemOptionLabel(currentMovementItem)
      setSearchValue({
        label: currentMovementItem.name || label,
        option: { uid: `${currentMovementItem.uid || ''}` }
      })
      setDisableSearch(true)
      setSelectedItem(true)
      form.setFieldsValue({
        ...currentMovementItem,
        unit_price: currentMovementItem.unit_price,
        discount_rate: currentMovementItem.discount_rate || 0,
        display: currentMovementItem.display || (currentMovementItem.register_type === 'SERVICE' ? "SERVICIO" : "UND")
      })
      updateValues(currentMovementItem)
    }
  }, [currentMovementItem, isEdit])

  const initializatePending = () => {
    if (itemList.length === 0) {
      console.log("Creado pending")
      newPending = true
      return {
        id: getNextId(),
        company_id: company.current?.id,
        created_by_user_id: user.id,
        created_by_user_name: user.name,
        created_by_user_document: user.document_number,
        document_number: `${getMovementPrefix(register.currentMovementType || "")}${createId(6)}`,
        invoice_type: "SIMPLE_RECEIPT" as const,
        document_type: (register.currentMovementType || "PURCHASE_RECEIPT") as any,
        status: movementStatus.PENDING
      }
    } else {
      newPending = false
      console.log("Tomado del pending: ", currentPending)
      return {
        ...currentPending,
        id: currentPending.id || getNextId(),
        uid: currentPending.uid,
        document_type: (currentPending.document_type || register.currentMovementType || "PURCHASE_RECEIPT") as any,
        status: currentPending.status || movementStatus.PENDING
      }      
    }
  }

  const getMovementConfig = (pending: any) => {
    if (newPending) {
      pending.document_type = register.currentMovementType
      pending.status = movementStatus.PENDING

      const { createDate, ...tmpOrder } = pending
      return {
        path: '/inventory/movement/create-movement',
        data: {
          movement: tmpOrder,
          company: company.current?.uid
        }
      }
    } else {
      const { id, createDate, uid, ...tmpOrder } = pending
      return {
        path: '/inventory/movement/update-movement',
        data: {
          company: company.current?.uid,
          uidMovement: pending.uid,
          movement: tmpOrder,
          module: config.module
        }
      }
    }
  }

  const onFinishForm = async (values: IMovementItemForm) => {
    setLoadingOk(true)
    if (!isEdit && !storageElement.id) {
      fail("No se ha seleccionado ningún elemento")
      setLoadingOk(false)
      return
    }
    if (!values.quantity || values.quantity <= 0) {
      fail("La cantidad debe ser mayor a 0")
      setLoadingOk(false)
      return
    }
    let pending: Partial<IMovementRS> = { ...initializatePending() }
    let movementElementItem: Partial<IMovementElementItem> = { ...values }
    movementElementItem.document_id = pending.id!
    movementElementItem.name = isEdit ? currentMovementItem.name : storageElement.name
    const type = mod === modules.RegisterEntry ? "PURCHASE_TAX" : "SELL_TAX"
    if (isEdit) {
      const idx = currentMovementItem.register - 1
      itemList[idx] = {
        ...currentMovementItem,
        ...values,
        display: values.display || currentMovementItem.display || (currentMovementItem.register_type === 'SERVICE' ? 'SERVICIO' : 'UND'),
        track_inventory: currentMovementItem.register_type !== 'SERVICE'
      }
      const taxes_list = getItemTaxes(itemList[idx], taxesOptions, type)
      taxes_list.forEach(tax => tax.tax_amount = roundValue(tax.tax_amount))
      const total_tax_amount = roundValue(taxes_list.reduce((acc, obj) => acc + obj.tax_amount, 0))
      itemList[idx].taxes_list = taxes_list
      itemList[idx].total_tax_amount = total_tax_amount
      itemList[idx].discount_amount = roundValue(itemList[idx].discount_rate * itemList[idx].total_amount / 100)
      itemList[idx].total_amount_full = roundValue(itemList[idx].total_amount + total_tax_amount)
      itemList[idx].total_amount_net = roundValue(itemList[idx].total_amount - (itemList[idx].discount_amount || 0) + total_tax_amount)
      console.log("Registro a editar", itemList[idx])
    } else {
      console.log("Registro a agregar a la lista:", storageElement, "SearchValue:", searchValue)
      movementElementItem.id = storageElement.id
      movementElementItem.uid = storageElement.uid
      movementElementItem.custom_id = storageElement.custom_id
      movementElementItem.key = createId(6)
      movementElementItem.register_type = storageElement.register_type
      movementElementItem.track_inventory = storageElement.register_type !== 'SERVICE'
      movementElementItem.display = values.display || storageElement.display || (storageElement.register_type === 'SERVICE' ? 'SERVICIO' : 'UND')
      movementElementItem.inventory = storageElement.inventory
      movementElementItem.register = itemList.length + 1
      movementElementItem.module_options = storageElement.module_options || []
      const taxes_list = getItemTaxes(movementElementItem as IMovementElementItem, taxesOptions, type)
      taxes_list.forEach(tax => tax.tax_amount = roundValue(tax.tax_amount))
      const total_tax_amount = roundValue(taxes_list.reduce((acc, obj) => acc + obj.tax_amount, 0))
      movementElementItem.taxes_list = taxes_list
      movementElementItem.total_tax_amount = total_tax_amount
      movementElementItem.discount_amount = roundValue((movementElementItem.discount_rate || 0) * (movementElementItem.total_amount || 0) / 100)
      movementElementItem.total_amount_full = roundValue((movementElementItem.total_amount || 0) + total_tax_amount)
      movementElementItem.total_amount_net = roundValue((movementElementItem.total_amount || 0) - (movementElementItem.discount_amount || 0) + total_tax_amount)
      itemList.push({ ...movementElementItem as IMovementElementItem })
    }
    pending.item_list = itemList
    pending.items = itemList.length
    const subTotal = itemList.reduce((acc, obj) => acc + obj.total_amount, 0)
    const totalDiscount = itemList.reduce((acc, obj) => acc + obj.discount_amount, 0)
    const totalTaxAmount = itemList.reduce((acc, obj) => acc + obj.total_tax_amount, 0)
    const totalTaxesList = getTotalTaxes(itemList)
    pending.total_amount = roundValue(subTotal)
    pending.total_discount_amount = roundValue(totalDiscount)
    pending.total_tax_amount = roundValue(totalTaxAmount)
    pending.taxes_list = totalTaxesList
    pending.taxes_list.forEach(tax => tax.tax_amount = roundValue(tax.tax_amount))
    pending.total_amount_full = roundValue(subTotal + totalTaxAmount)
    pending.total_amount_net = roundValue(subTotal + totalTaxAmount - totalDiscount)

    const { path, data } = getMovementConfig(pending)
    const movementResponse = await securePost(path, data)
    if (movementResponse.status === 200) {
      pending.item_list = itemList
      if (!pending.uid) {
        pending.uid = movementResponse.movementUid
      }
      pending.document_type = (register.currentMovementType || pending.document_type || "PURCHASE_RECEIPT") as any
      const uniquesArray = addToUniquesArray(pendingList, pending, pending.id || pending.uid, "start")
      if (mod === modules.RegisterEntry) {
        updateRegister({ pendingListEntries: uniquesArray, itemListEntries: itemList })
      }
      if (mod === modules.RegisterExit) {
        updateRegister({ pendingListExits: uniquesArray, itemListExits: itemList })
      }
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current)
      }
      setSearchValue({ label: "", option: { uid: "" } })
      setSelectedItem(false)
      setDisableSearch(false)
      setStorageElement(defaultStorageItemValues)
      updateRegister({ currentMovementItem: undefined, showAddItem: false, currentPending: pending as IMovementRS })
      form.setFieldsValue({})
      form.resetFields()
      mutateMovementQueries()
      success('Registro creado con éxito')
    } else {
      fail("Por favor intente de nuevo")
    }
    setLoadingOk(false)
    console.log("values:", values, "pending:", pending)
  }

  const success = (content: string) => {
    messageApi.open({
      type: 'success',
      content,
    })
  }

  const fail = (content: string) => {
    messageApi.open({
      type: 'error',
      content,
    })
  }

  const handleValuesChange: FormProps["onValuesChange"] = (changedValues, allValues) => {
    if ("quantity" in changedValues || "unit_price" in changedValues || "discount_rate" in changedValues) {
      updateValues(allValues)
    }
  }

  function updateValues(values: any) {
    const taxSubtype = mod === modules.RegisterEntry ? "PURCHASE_TAX" : "SELL_TAX"
    const totalUnits = (values?.quantity || 0)
    const unitPrice = (values?.unit_price || 0)
    const total = unitPrice * totalUnits
    const moduleOptions = ((isEdit ? currentMovementItem?.module_options : storageElement?.module_options) || [])
    const allTaxes = moduleOptions
      .filter((e: any) => e?.is_active && e?.sub_type === taxSubtype)
      .map((e: any) => taxesOptions?.find(tax => tax.id === e.option_id))
      .filter(Boolean)
    console.log("Alltaxes", allTaxes)
    const totalRateTax = allTaxes.reduce((sum: number, el: any) => el?.type === "TAX_RATE" ? sum + (Number(el?.numeric_value) || 0) : sum, 0)
    const totalFixedTax = allTaxes.reduce((sum: number, el: any) => el?.type === "TAX_FIXED" ? sum + (Number(el?.numeric_value) || 0) : sum, 0)
    const discountRateVal = values?.discount_rate || 0
    const fullPrice = roundValue(total * (1 + totalRateTax / 100) + totalFixedTax)
    const netPrice = roundValue(total * (1 - discountRateVal / 100) * (1 + totalRateTax / 100) + totalFixedTax)
    form.setFieldsValue({
      total_amount: roundValue(total),
      total_amount_full: fullPrice,
      total_amount_net: netPrice
    })
  }

  const debouncedSearch = (text: string) => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current)
    }
    searchTimeoutRef.current = setTimeout(() => {
      void executeSearch(text)
    }, 400)
  }

  const handleFocus = () => {
    if (options.length === 0 && !disableSearch && !selectedItem) {
      void executeSearch("")
    }
  }

  const handleChange = (text: any, option: any) => {
    const labelText = typeof text === 'string' ? text : (option?.value || option?.label || '')
    setSearchValue({ label: labelText, option: option || { uid: "" } })

    const searchLower = labelText.toLowerCase().trim()
    const filteredItems = storageElementList.filter((item: any) => {
      if (activeTypeFilter !== "ALL" && item.register_type !== activeTypeFilter) return false
      if (!searchLower) return true
      const nameMatch = (item.name || '').toLowerCase().includes(searchLower)
      const skuMatch = (item.sku || '').toLowerCase().includes(searchLower)
      const customIdMatch = String(item.custom_id || '').toLowerCase().includes(searchLower)
      const barcodeMatch = (item.barcode || '').toLowerCase().includes(searchLower)
      const categoryMatch = (item.category || '').toLowerCase().includes(searchLower)
      const displayMatch = (item.display || '').toLowerCase().includes(searchLower)
      const descMatch = (item.description || '').toLowerCase().includes(searchLower)
      return nameMatch || skuMatch || customIdMatch || barcodeMatch || categoryMatch || displayMatch || descMatch
    })

    const filteredOptions = filteredItems.map((item: any) =>
      createOption(item, mod, register.categoryOptions, register.displayOptions, register.umOptions)
    )
    setOptions(filteredOptions)

    if (!disableSearch && !selectedItem) {
      debouncedSearch(labelText)
    }
  }

  const handleSearch = (event: any) => {
    let text = ""
    if (event && typeof event === 'object' && 'target' in event && event.target) {
      event.preventDefault()
      text = event.target.value
    } else if (typeof event === 'string') {
      text = event
    } else {
      text = searchValue.label
    }

    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current)
    }
    void executeSearch(text)
  }

  const handleSelect = async (text: any, option: any) => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current)
    }
    const selected = storageElementList.find(el => el.uid === option.uid) || option.rawItem
    console.log("selected: ", selected)
    if (selected) {
      if (mod === modules.RegisterEntry && !selected.purchase_item) {
        return fail("El elemento no esta habilitado para ingresos")
      }
      if (mod === modules.RegisterExit && !selected.sell_item) {
        return fail("El elemento no esta habilitado para salidas")
      }
      setLoadingOk(true)
      const itemResponse = await securePost('/inventory/item/get-item', {
        company: company.current?.uid,
        uidItem: option.uid || selected.uid,
        module: config.module
      })
      setLoadingOk(false)
      if (itemResponse.status === 200 && itemResponse.inventoryItem) {
        console.log("item found: ", itemResponse.inventoryItem)
        setStorageElement(itemResponse.inventoryItem)
      } else {
        return fail("No se pudo obtener el elemento")
      }
      const labelText = getItemOptionLabel(selected)
      setSearchValue({ label: labelText, option })
      const base = mod === modules.RegisterEntry ? itemResponse.inventoryItem?.purchase_price : itemResponse.inventoryItem?.sell_price
      const discount = mod === modules.RegisterEntry ? itemResponse.inventoryItem?.purchase_discount : itemResponse.inventoryItem?.sell_discount
      const isService = itemResponse.inventoryItem?.register_type === 'SERVICE' || selected.register_type === 'SERVICE'
      form.setFieldsValue({
        unit_price: base || 0,
        quantity: 0,
        discount_rate: discount || 0,
        display: selected.display || (isService ? 'SERVICIO' : 'UND')
      })
      setDisableSearch(true)
      setSelectedItem(true)
    }
  }

  const handleClearSelection = () => {
    setSearchValue({ label: "", option: { uid: "" } })
    setSelectedItem(false)
    setDisableSearch(false)
    setStorageElement(defaultStorageItemValues)
    form.setFieldsValue({
      name: "",
      unit_price: 0,
      quantity: 0,
      discount_rate: 0,
      display: "",
      total_amount: 0,
      total_amount_full: 0,
      total_amount_net: 0,
    })
    void executeSearch("")
  }

  const handleSaveItem = async (values: StorageItemBasic): Promise<any> => {
    if (!company?.current?.uid || !company.current?.id) {
      throw new Error('No se encontró la compañía activa')
    }

    const isService = values.register_type === 'SERVICE'
    const prices: any[] = []
    if (values.sell_price !== undefined && values.sell_price !== null) {
      prices.push({
        id: -1,
        element_id: 0,
        type: 'SELL',
        value: values.sell_price,
        description: 'Precio de venta',
        is_active: true,
        is_default: true,
        is_visible: true,
      })
    }
    if (values.purchase_price !== undefined && values.purchase_price !== null) {
      prices.push({
        id: -1,
        element_id: 0,
        type: 'PURCHASE',
        value: values.purchase_price,
        description: 'Precio de compra',
        is_active: true,
        is_default: true,
        is_visible: true,
      })
    }

    const moduleOptions: any[] = []
    const categoryKey = values.category || 'VRS'
    const displayKey = isService ? 'N/A' : 'UND'

    const itemTypes = register.itemTypes?.length ? register.itemTypes : (company.moduleOptions?.filter((el: any) => el.type === 'ITEM_TYPE') || [])
    const categoryOptionsList = register.categoryOptions?.length ? register.categoryOptions : (company.moduleOptions?.filter((el: any) => el.type === 'CATEGORY_OPTION') || [])
    const displayOptionsList = register.displayOptions?.length ? register.displayOptions : (company.moduleOptions?.filter((el: any) => el.type === 'DISPLAY_OPTION') || [])
    const umOptionsList = register.umOptions?.length ? register.umOptions : (company.moduleOptions?.filter((el: any) => el.type === 'UM') || [])

    const idItemType = itemTypes?.find((el: any) => el.key === values.register_type)?.id
    const idCategory = categoryOptionsList?.find((el: any) => el.key === categoryKey)?.id
    const idDisplay = displayOptionsList?.find((el: any) => el.key === displayKey)?.id
    const idUm = umOptionsList?.find((el: any) => el.key === (isService ? 'UND' : values.measure_unit) || (values.measure_unit === 'UN' && el.key === 'UND'))?.id

    if (idItemType) {
      moduleOptions.push({
        id: -1,
        option_id: idItemType,
        is_active: true,
        sub_type: 'TYPE_ELEMENT',
        category: 'NATIONAL',
      })
    }
    if (idCategory) {
      moduleOptions.push({
        id: -1,
        option_id: idCategory,
        is_active: true,
        sub_type: 'CATEGORY_ELEMENT',
        category: 'NATIONAL',
      })
    }
    if (idDisplay) {
      moduleOptions.push({
        id: -1,
        option_id: idDisplay,
        is_active: true,
        sub_type: 'DISPLAY_ELEMENT',
        category: 'NATIONAL',
      })
    }
    if (idUm) {
      moduleOptions.push({
        id: -1,
        option_id: idUm,
        is_active: true,
        sub_type: 'UM_ELEMENT',
        category: 'NATIONAL',
      })
    }

    const newStorageElement: any = {
      company_id: company.current.id,
      company_uid: company.current.uid,
      name: values.name,
      description: values.description || '',
      is_active: true,
      is_traceable: isService ? false : Boolean(values.is_traceable),
      sell_item: values.sell_item ?? true,
      purchase_item: values.purchase_item ?? (mod === modules.RegisterEntry),
      register_type: values.register_type,
      measure_unit: isService ? 'UN' : (values.measure_unit || 'UN'),
      category: values.category || 'VRS',
      display: isService ? 'N/A' : 'UND',
      location: isService ? 'N/A' : '',
      image: '',
      image_mini: '',
      quantity: 0,
      sell_price: values.sell_price || 0,
      purchase_price: values.purchase_price || 0,
      sell_discount: 0,
      purchase_discount: 0,
      custom_id: values.custom_id || '',
      sku: values.sku || '',
      barcode: '',
      module_options: moduleOptions,
      prices,
      other_values: [],
      inventory: [],
    }

    const response = await securePost('/inventory/item/create-item', {
      company: company.current.uid,
      storageElement: newStorageElement,
    })

    if (response?.item || response?.storageElement) {
      return response.item || response.storageElement
    } else {
      throw new Error(response?.message || 'Error al guardar el elemento')
    }
  }

  const handleCreateItemSuccess = (savedItem: any) => {
    setShowItemModal(false)
    success('Registro creado con éxito')
    setStorageElementList(prev => [savedItem, ...prev])
    setStorageElement(savedItem)
    const labelText = getItemOptionLabel(savedItem)
    const option = createOption(savedItem, mod, register.categoryOptions, register.displayOptions, register.umOptions)
    setSearchValue({ label: labelText, option })
    const base = mod === modules.RegisterEntry ? savedItem.purchase_price : savedItem.sell_price
    const discount = mod === modules.RegisterEntry ? savedItem.purchase_discount : savedItem.sell_discount
    form.setFieldsValue({
      name: labelText,
      unit_price: base || 0,
      quantity: 0,
      discount_rate: discount || 0,
      display: savedItem.display || 'UND',
    })
    setDisableSearch(true)
    setSelectedItem(true)
  }
  
  const onCancel = () => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current)
    }
    setSearchValue({ label: "", option: { uid: "" } })
    updateRegister({ showAddItem: false, currentMovementItem: undefined })
    setSelectedItem(false)
    setDisableSearch(false)
    setStorageElement(defaultStorageItemValues)
    form.resetFields()
  }

  const handleDelete = async () => {
    if (itemList.length === 1) {
      return fail("Debe incluir por lo menos un elemento")
    }
    setLoadingDelete(true)
    const list = itemList.filter((el) => el.key !== currentMovementItem.key).map((me, i) => ({
      ...me,
      register: i + 1
    }))
    const subTotal = list.reduce((acc, obj) => acc + obj.total_amount, 0)
    const taxAmount = list.reduce((acc, obj) => acc + obj.total_tax_amount, 0)
    const totalDiscount = list.reduce((acc, obj) => acc + obj.discount_amount, 0)
    const totalTaxesList = getTotalTaxes(list)
    currentPending.total_amount = roundValue(subTotal)
    currentPending.total_tax_amount = roundValue(taxAmount)
    currentPending.total_discount_amount = roundValue(totalDiscount)
    currentPending.taxes_list = totalTaxesList
    currentPending.taxes_list.forEach(tax => tax.tax_amount = roundValue(tax.tax_amount))
    currentPending.total_amount_full = roundValue(subTotal + taxAmount)
    currentPending.total_amount_net = roundValue(subTotal + taxAmount - totalDiscount)
    const path = '/inventory/movement/update-movement'
    const data = {
      company: company.current?.uid,
      uidMovement: currentPending.uid,
      movement: {
        item_list: list,
        items: list.length,
        total_amount: roundValue(subTotal),
        tax_amount: roundValue(taxAmount),
        total_amount_full: roundValue(subTotal + taxAmount),
        total_amount_net: roundValue(subTotal + taxAmount - totalDiscount)
      },
      module: config.module
    }
    const movementResponse = await securePost(path, data)
    if (movementResponse.status === 200) {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current)
      }
      setSearchValue({ label: "", option: { uid: "" } })
      setSelectedItem(false)
      setDisableSearch(false)
      setStorageElement(defaultStorageItemValues)
      const newPending = {
        ...currentPending,
        item_list: list,
        document_type: (currentPending.document_type || register.currentMovementType || "PURCHASE_RECEIPT") as any
      }
      const itemListKey = mod === modules.RegisterEntry ? "itemListEntries" : "itemListExits"
      updateRegister({
        currentMovementItem: undefined,
        showAddItem: false,
        currentPending: newPending,
        [itemListKey]: list
      })
      form.setFieldsValue({})
      form.resetFields()
      mutateMovementQueries()
      success('Registro eliminado con éxito')
    } else {
      fail("Por favor intente de nuevo")
    }
    setLoadingDelete(false)
  }

  const discountRate = Form.useWatch("discount_rate", form) || 0
  const isServiceItem = isEdit
    ? currentMovementItem?.register_type === 'SERVICE'
    : storageElement?.register_type === 'SERVICE'

  const modalFooter = isEdit ? [
    <Button key="cancel" onClick={onCancel}>Cancelar</Button>,
    <Button key="delete" loading={loadingDelete} danger onClick={handleDelete}>Eliminar</Button>,
    <Button key="ok" loading={loadingOk} type="primary" htmlType="submit" form="add-entry-item">
      Actualizar
    </Button>,
  ] : [
    <Button key="cancel" onClick={onCancel}>Cancelar</Button>,
    <Button key="ok" loading={loadingOk} type="primary" htmlType="submit" form="add-entry-item">
      Agregar
    </Button>,
  ]

  const modalTitle = mod === modules.RegisterEntry
    ? (isEdit ? "Editar Elemento en Compra" : "Agregar a la Orden de Compra")
    : (isEdit ? "Editar Elemento" : "Agregar Registro")

  const handleSelectCommonService = (serviceName: string) => {
    const searchPart = serviceName.toLowerCase().split('/')[0].trim()
    const match = storageElementList.find(
      (el: any) => el.register_type === 'SERVICE' && (el.name || '').toLowerCase().includes(searchPart)
    )
    if (match) {
      const opt = createOption(match, mod, register.categoryOptions, register.displayOptions, register.umOptions)
      void handleSelect(match.name, opt)
    } else {
      setModalPreType('SERVICE')
      setModalPreName(serviceName)
      setShowItemModal(true)
    }
  }

  const typeFilterOptions = [
    { label: '🌐 Todos', value: 'ALL' },
    ...(register.itemTypes?.filter((t: any) => t.show) || []).map((t: any) => {
      if (t.key === 'PRODUCT') return { label: '📦 Mercancía / Insumos', value: 'PRODUCT' }
      if (t.key === 'SERVICE') return { label: '🚚 Servicios (Fletes, Mano de Obra)', value: 'SERVICE' }
      if (t.key === 'FIXED_ASSET') return { label: '🏷️ Activos Fijos', value: 'FIXED_ASSET' }
      return { label: t.string_value || t.key, value: t.key }
    })
  ]

  const searchPlaceholder = activeTypeFilter === 'SERVICE'
    ? "Buscar servicio (ej. Flete, Mano de obra, Grúa)..."
    : activeTypeFilter === 'PRODUCT'
    ? "Buscar mercancía o insumo físico..."
    : "Buscar elemento en catálogo (producto, insumo, flete, servicio)..."

  return (
    <Modal 
      title={<div style={{ textAlign: "center", fontWeight: 600 }}>{modalTitle}</div>} 
      open={register.showAddItem}
      maskClosable={false}
      onCancel={onCancel}
      className="modal-responsive-md"
      destroyOnHidden={true}
      footer={modalFooter}
    >
      {contextHolder}
      <Form
        form={form}
        layout="vertical"
        name="add-entry-item"
        onValuesChange={handleValuesChange}
        scrollToFirstError={{ block: 'end', focus: true }}
        style={{ maxWidth: '800px', margin: '0 auto' }}
        onFinish={onFinishForm}
      >
        <MovementItemSearchSection
          isEdit={isEdit}
          selectedItem={selectedItem}
          isServiceItem={isServiceItem}
          typeFilterOptions={typeFilterOptions}
          activeTypeFilter={activeTypeFilter}
          onTypeFilterChange={(val) => {
            setActiveTypeFilter(val)
            activeTypeFilterRef.current = val
            void executeSearch(searchValue.label || "", val)
          }}
          mod={mod}
          onSelectCommonService={handleSelectCommonService}
          onClearSelection={handleClearSelection}
          searchValue={searchValue}
          onChangeSearchValue={handleChange}
          onSelectOption={handleSelect}
          onFocusSearch={handleFocus}
          onSearchInput={handleSearch}
          options={options}
          loadingSearch={loadingSearch}
          disableSearch={disableSearch}
          searchPlaceholder={searchPlaceholder}
          onOpenCreateItemModal={(type) => {
            setModalPreType(type)
            setModalPreName(undefined)
            setShowItemModal(true)
          }}
          onOpenInventoryModal={() => updateRegister({ showInventory: true })}
        />

        <MovementItemFormFields
          selectedItem={selectedItem}
          isEdit={isEdit}
          isServiceItem={isServiceItem}
          discountRate={discountRate}
          inventoryData={isEdit ? (currentMovementItem?.inventory || []) : (storageElement?.inventory || [])}
        />
      </Form>

      <UnifiedItemModal
        visible={showItemModal}
        onCancel={() => {
          setShowItemModal(false)
          setModalPreType(undefined)
          setModalPreName(undefined)
        }}
        onSuccess={handleCreateItemSuccess}
        onSave={handleSaveItem}
        defaultRegisterType={modalPreType}
        defaultName={modalPreName}
        itemTypes={(register.itemTypes?.length ? register.itemTypes : (company.moduleOptions?.filter((el: any) => el.type === 'ITEM_TYPE') || [])).filter((o: any) => o.show)}
        umOptions={(register.umOptions?.length ? register.umOptions : (company.moduleOptions?.filter((el: any) => el.type === 'UM') || [])).filter((o: any) => o.show)}
        categoryOptions={(register.categoryOptions?.length ? register.categoryOptions : (company.moduleOptions?.filter((el: any) => el.type === 'CATEGORY_OPTION') || [])).filter((o: any) => o.show)}
      />
    </Modal>
  )
}

export default RegisterMovementModal